import PropTypes from 'prop-types';
import React, { useEffect, memo, useState, useRef } from 'react';
import cuid from 'cuid';
import classNames from 'classnames';
import { getElementHeight } from 'ctm-util-functions';
import { debounce } from '../components/form/utils';
import { ScrollWrapper } from './scroll_wrapper';

import styles from './affix_wrapper.css';

const AffixWrapper = memo((
    {   shouldAffix = true,
        affixVariant,
        nonAffixVariant,
        hiddenWhenNotAffixed = false,
        includeBuffer = false,
        children }
) => {
    const [isAffixed, setIsAffixed] = useState(false);
    const [heightOfOtherAffixedElements, setHeightOfOtherAffixedElements] = useState(0);
    const [shouldHandleScroll, setShouldHandleScroll] = useState(false);
    const [affixedContainer, setAffixedContainer] = useState(null);
    const affixedOffset = useRef(0);
    const affixedHeight = useRef(0);
    const affixHandle = useRef(`affix_${cuid()}`);
    const animationFrame = useRef(null);

    /**
     * trigger the setup method on mount
     */
    useEffect(() => {
        if (typeof window === 'undefined') {
            return () => undefined;
        }

        // if the scroll to animation has not yet completed run a timeout of the length of the
        // scroll animation so we ensure the affixed container calculates the correct top offset
        // as React batches updates to the DOM, componentDidMount can run faster than the element is in the DOM,
        // which means we need to do a setTimeout as we need to calculate height.
        animationFrame.current = window.requestAnimationFrame(() => {
            const timeout = window.scrollY !== 0 ? 500 : 0;
            setTimeout(setupAffixState, timeout);
        });

        return () => {
            if (animationFrame.current) {
                window.cancelAnimationFrame(animationFrame.current);
            }
        };
    }, [affixedContainer]);

    /**
     * On component mount, store a reference to the wrapper's dom element for scroll / height calculations
     */
    const setupAffixState = () => {
        // select and store the affixed container's dom element
        setAffixedContainer(document.getElementById(affixHandle.current));

        // get the offset from the container to the top of the page
        affixedOffset.current = getVerticalOffset(affixedContainer);
        affixedOffset.current = affixedOffset.current < 0 ? 0 : affixedOffset.current;
        // @todo not sure how this will go with multiple containers
        affixedHeight.current = getElementHeight(affixedContainer);
        setHeightOfOtherAffixedElements(getOtherAffixed());
        // This delays the handleScroll event from running when the page is initially scrolling to the top.
        setShouldHandleScroll(true);
    };

    const getVerticalOffset = (el) => {
        let offset = el.offsetTop;
        let _el = el;
        while (_el.offsetParent) {
            _el = _el.offsetParent;
            offset += _el.offsetTop;
        }
        return offset;
    };

    const getOtherAffixed = () => {
        const container = document.querySelector('[data-layout-extends=container_full_width]');
        if (!container) return 0;

        const elementsAbove = [];
        let sibling = container.previousElementSibling;
        while (sibling) {
            elementsAbove.push(sibling);
            sibling = sibling.previousElementSibling;
        }
        const stickyElementsAbove = elementsAbove.filter((el) => window.getComputedStyle(el).position === 'sticky');

        return stickyElementsAbove.reduce((acc, el) => (acc + el.getBoundingClientRect().height), 0);
    };

    /**
     * The handle scroll method to be passed to the scroll wrapper
     */
    const handleScroll = debounce(() => {
        // Due to SSR, we need this window check.
        if (typeof window === 'undefined' || !shouldHandleScroll) {
            return;
        }

        const offsetAndElements = window.scrollY + heightOfOtherAffixedElements;
        const viewportHeight = window.innerHeight;
        // Don't affix on short pages where there isn't much scroll (can cause jitteryness)
        // E.G. If the Viewport is 600px high and there's 50px of scroll available, then scrollHeight is 650px.
        // If you are affixing 100px, thus taking 100px out of the DOM, 600 < 500 == false so don't affix.
        // If there is 500px of scroll available, then scrollHeight is 1100px. 600 < 1000 == true, so affix.
        // We should also not exclude the affixed height from document height when its already affixed.
        const documentHeight = getElementHeight(window.document);
        const scrollHeightLessAffixedElements = isAffixed ? documentHeight : documentHeight - affixedHeight.current;
        // add more distance to ensure CTA is covered in results Feature View
        const buffer = includeBuffer ? 300 : 0;
        const pageIsAtTop = window.scrollY === 0;
        const isAffixedCondition = offsetAndElements > affixedOffset.current + buffer
            && viewportHeight < scrollHeightLessAffixedElements;

        setIsAffixed(!pageIsAtTop && isAffixedCondition);
    }, 5);

    /**
     * Render the component passed as children. This wrapper is purely for handling the assignment
     * of event listeners on scroll events, and affixed styling
     * @returns {Node} The component
     */
    const className = classNames({
        [styles.affix]: isAffixed,
        [styles.nonAffix]: !isAffixed,
        [styles[`variant_affix_${affixVariant}`]]: isAffixed && affixVariant,
        [styles[`variant_non_affix_${nonAffixVariant}`]]: !isAffixed && nonAffixVariant,
        [styles.hidden_when_not_affixed]: hiddenWhenNotAffixed && !isAffixed
    });

    return (
        <>
            <div id={affixHandle.current} className={className} style={{ top: `${heightOfOtherAffixedElements}px` }}>
                <ScrollWrapper resizeId="features_affixed"
                    handleScroll={shouldAffix ? handleScroll : null}>
                    {children}
                </ScrollWrapper>
            </div>
            {isAffixed && <div style={{ height: affixedHeight.current }} />}
        </>
    );
});

AffixWrapper.propTypes = {
    shouldAffix: PropTypes.bool,
    affixVariant: PropTypes.string,
    nonAffixVariant: PropTypes.string,
    includeBuffer: PropTypes.bool,
    hiddenWhenNotAffixed: PropTypes.bool,
    children: PropTypes.node
};

export { AffixWrapper };
